import _ from 'underscore';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import produce from 'immer';
import {
	format,
	isAfter,
	isBefore,
	differenceInHours,
	eachDayOfInterval,
	formatISO,
	isPast,
	isFuture,
	isEqual,
	parseISO,
	startOfDay,
	endOfDay
} from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import { getNumericDeclension } from '@culture/common-helpers/string';
import { deepClone } from '@fit/object-utils';

import DatePicker from 'components/InlineCalendar/components/DatePicker';
import FilterModal from 'components/filters/FilterModal';
import Icon from 'components/Icon';
import Popup from 'components/Popup';
import SizeDependedBlock from 'components/SizeDependedBlock';

import PlaceItem from './components/PlaceItem';
import Divider from './components/Divider';

import styles from './styles.module.scss';

const findClosestEvent = (params) => {
	const { places, seances, locale, isKino } = params;
	const minDate = startOfDay(new Date());

	const locationPlacesIds = produce([], (draft) => {
		_(places).each((place) => {
			if (place.locale.sysName.match(locale)) {
				draft.push(place._id);
			}
		});

		if (!draft.length) {
			draft.push(..._(seances).pluck('placeId'));
		}
	});

	const locationSeances = _(seances).filter(
		(seance) => locationPlacesIds.indexOf(seance.placeId) >= 0
	);

	const minSeanceStartDates = produce([], (draft) => {
		_(locationSeances).each((seance) => {
			const difference = differenceInHours(
				new Date(seance.endDate),
				new Date(seance.startDate)
			);

			const isLongPeriod =
				difference >= 24 &&
				isPast(new Date(seance.startDate)) &&
				isFuture(new Date(seance.endDate));

			if (new Date(seance.startDate) >= minDate) {
				draft.push(new Date(seance.startDate));
			} else if (isKino && isLongPeriod) {
				draft.push(new Date(seance.startDate));
			}
		});
	});

	if (!_.isEmpty(minSeanceStartDates)) {
		return _.min(minSeanceStartDates);
	}
	return null;
};

const EventSchedule = ({ event, locale, scrollTo, showShareModal = false }) => {
	const [mainPlacesData, setMainPlaces] = useState([]);
	const [otherPlacesData, setOtherPlaces] = useState([]);
	const [showOtherPlaces, setShowOtherPlaces] = useState([]);
	const [calendarIsOpen, setCalendarIsOpen] = useState(false);
	const [otherPlacesStatus, setOtherPlacesStatus] = useState(false);
	const [emptySeancesStatus, setEmptySeancesStatus] = useState(false);
	const [pastStatus, setPastStatus] = useState(false);
	const [dates, setDates] = useState({
		startDate: null,
		endDate: null
	});

	const controlRef = useRef();

	const { startDate, endDate } = dates;

	const defaultMaxDate = new Date();
	const defaultStartDate = new Date();
	const countOfHideOtherPlaces =
		otherPlacesData.length - showOtherPlaces.length;

	const isKino = !!_(event.genres).findWhere({ name: 'kino' });
	const dateLocale = { locale: ruLocale };
	const placesWordVariants = {
		single: 'место',
		some: 'места',
		many: 'мест'
	};

	defaultMaxDate.setFullYear(defaultMaxDate.getFullYear() + 1);

	const closeCalendarPopup = () => setCalendarIsOpen(false);
	const switchCalendarPopUp = () => setCalendarIsOpen((prev) => !prev);

	const otherPlacesHandler = () => {
		const openNewBunchOfPlaces = () =>
			setShowOtherPlaces(otherPlacesData.slice(0, showOtherPlaces.length + 5));

		if (showOtherPlaces.length === otherPlacesData.length) {
			setOtherPlacesStatus((prev) => !prev);
			setShowOtherPlaces([]);
			scrollTo();
			return;
		}
		if (otherPlacesStatus) {
			openNewBunchOfPlaces();
		} else {
			setOtherPlacesStatus((prev) => !prev);
			openNewBunchOfPlaces();
		}
	};

	const availableDates = event.seances?.reduce((seances, seance) => {
		if (isFuture(parseISO(seance.startDate))) {
			seances.push(seance.startDate);
		}
		return seances;
	}, []);

	const filterPlacesHandler = (places) => {
		let mainPlaces;
		let otherPlaces;

		const filterPlaces = (callback) =>
			places.filter((element) => callback(element));
		const isMatch = (value) => value.locale.sysName.match(locale);
		const allPlacesWithSeances = filterPlaces((value) => value.seances.length);
		const distributeValue = () => {
			mainPlaces = [_.head(allPlacesWithSeances)];
			otherPlaces = _.tail(allPlacesWithSeances);
		};

		if (locale && locale !== 'russia') {
			mainPlaces = filterPlaces(
				(value) => isMatch(value) && value.seances.length
			);
			if (!mainPlaces.length) {
				distributeValue();
			} else {
				otherPlaces = filterPlaces(
					(value) => !isMatch(value) && value.seances.length
				);
			}
		} else {
			distributeValue();
		}

		setMainPlaces(mainPlaces);
		setOtherPlaces(otherPlaces);
	};

	const findClosestEventWithSeances = (places, seances) =>
		findClosestEvent({
			places,
			seances,
			isKino,
			locale
		});

	const getDifferenceSeance = (seance) => {
		const seanceStartDate = new Date(seance.startDate);
		const seanceEndDate = new Date(seance.endDate);

		return differenceInHours(seanceEndDate, seanceStartDate);
	};

	const filterByDates = (data, startValue, endValue) => {
		const fullCopy = deepClone(data);

		let seancesLength = 0;
		let end = new Date(endValue) ?? undefined;
		const start = new Date(startValue);

		if (!end) {
			end = endOfDay(new Date(start));
		}

		const finalResult = fullCopy.places
			.map((place) => {
				const copy = _.clone(place);
				let prepareSeances;

				const placeSeances = _(fullCopy.seances).filter(
					(seance) => seance.placeId === place._id
				);
				if (isKino && placeSeances.length === 1) {
					const difference = getDifferenceSeance(placeSeances[0]);

					const startDateSeance = isFuture(parseISO(placeSeances[0].startDate))
						? parseISO(placeSeances[0].startDate)
						: new Date();

					const intervalDays =
						difference >= 24 &&
						eachDayOfInterval({
							start: startDateSeance,
							end: parseISO(placeSeances[0].endDate)
						});

					prepareSeances = intervalDays
						? intervalDays.map((day) => ({
								startDate: formatISO(day),
								endDate: formatISO(endOfDay(day))
						  }))
						: placeSeances;
				} else {
					prepareSeances = placeSeances;
				}

				copy.seances = prepareSeances.filter((seance) => {
					const seanceStartDate = new Date(seance.startDate);
					const seanceEndDate = new Date(seance.endDate);
					const difference = getDifferenceSeance(seance);

					if (isKino && difference >= 24) {
						return (
							(isFuture(seanceEndDate) && isPast(seanceStartDate)) ||
							isFuture(seanceStartDate)
						);
					}

					return (
						(isAfter(seanceStartDate, start) ||
							isEqual(seanceStartDate, start)) &&
						isBefore(seanceStartDate, end)
					);
				});

				if (copy.seances.length >= seancesLength) {
					seancesLength = copy.seances.length;
				}

				return copy;
			})
			.filter(Boolean);

		setEmptySeancesStatus(!seancesLength);

		filterPlacesHandler(finalResult);
	};

	const onDayApply = (data) => {
		const { startDate: start, endDate: end } = data;
		filterByDates(event, start, end || endOfDay(start));
		setCalendarIsOpen(false);
		setShowOtherPlaces([]);
	};

	const renderDateTitle = useMemo(() => {
		const dateFormat = 'd MMMM';
		const formatter = (date) => format(date, dateFormat, dateLocale);

		if (startDate && endDate) {
			const end = new Date(endDate).getDate();
			const start = new Date(startDate).getDate();

			if (start === end) {
				return `от ${formatter(startDate)}`;
			}

			return `от ${formatter(startDate)}
			до ${formatter(endDate)}`;
		}

		if (startDate) {
			return `${formatter(startDate)}`;
		}

		return `${formatter(defaultStartDate)}`;
	}, [startDate, endDate]);

	useEffect(() => {
		const closestDate = findClosestEventWithSeances(
			event.places,
			event.seances
		);

		if (!closestDate) {
			setPastStatus(true);
		} else {
			const start = startOfDay(closestDate);
			const end = endOfDay(closestDate);

			setDates({
				startDate: start,
				endDate: end
			});

			filterByDates(event, start, end);
		}
	}, [event]);

	if ((event.seances.length <= 1 && emptySeancesStatus) || pastStatus) {
		return <></>;
	}

	return (
		<div className={styles.eventSchedule}>
			<div className={styles.head}>
				<div className={styles.title}>Расписание</div>
				{event.seances.length > 1 && (
					// eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
					<div ref={controlRef} onClick={switchCalendarPopUp}>
						<span className={styles.dates}>
							{renderDateTitle}
							<Icon
								icon="arrow-down"
								className={cx(styles.datesIcon, {
									[styles.datesActive]: calendarIsOpen
								})}
							/>
						</span>
					</div>
				)}
			</div>
			<div>
				{!emptySeancesStatus &&
					mainPlacesData.map((place) => (
						<PlaceItem
							key={place?.eipskId}
							placeElement={place}
							{...{ event, isKino, showShareModal }}
						/>
					))}
				{emptySeancesStatus && (
					<div className={styles.emptyText}>
						К сожалению, событий по выбранной дате не найдено. Вы можете выбрать
						другую дату.
					</div>
				)}
				{!_(otherPlacesData).isEmpty() && (
					<div
						className={cx(styles.otherPlaces, {
							[styles.otherPlacesVisible]: otherPlacesStatus
						})}
					>
						{showOtherPlaces.map((elem) => (
							<PlaceItem
								key={elem?.eipskId}
								placeElement={elem}
								{...{ event, isKino }}
							/>
						))}
					</div>
				)}
				{!!otherPlacesData.length && (
					<>
						<Divider disabled />
						{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
						<button
							type="button"
							onClick={otherPlacesHandler}
							className={styles.similarPlace}
						>
							{countOfHideOtherPlaces ? (
								<>
									еще {countOfHideOtherPlaces}{' '}
									{getNumericDeclension(
										countOfHideOtherPlaces,
										placesWordVariants
									)}
								</>
							) : (
								<>свернуть</>
							)}
						</button>
					</>
				)}
				<SizeDependedBlock breakpoint="iphoneXs">
					<FilterModal
						modalStatus={calendarIsOpen}
						closeHandler={closeCalendarPopup}
						label="Календарь"
					>
						<DatePicker
							titleStatus={false}
							onDayApply={onDayApply}
							dates={dates}
							setDates={setDates}
							availableDates={availableDates}
						/>
					</FilterModal>
					<Popup
						isOpen={calendarIsOpen}
						element={controlRef.current}
						placement="bottom-start"
						theme="widget"
						onClickOutside={closeCalendarPopup}
						options={{
							strategy: 'absolute',
							modifiers: [
								{
									name: 'flip',
									options: {
										fallbackPlacements: ['bottom']
									}
								}
							]
						}}
					>
						<DatePicker
							onDayApply={onDayApply}
							dates={dates}
							setDates={setDates}
							availableDates={availableDates}
						/>
					</Popup>
				</SizeDependedBlock>
			</div>
		</div>
	);
};

EventSchedule.propTypes = {
	event: PropTypes.object,
	locale: PropTypes.string,
	scrollTo: PropTypes.func.isRequired,
	showShareModal: PropTypes.bool
};

export default EventSchedule;
